import React from 'react';

import Input from 'components/partials/input/input';

interface EditablePercentCellProps {
  value: number;
  onChange: (value: number) => void;
  isDirty?: boolean;
}

const formatPercent = (val: number): string => {
  const percent = Math.round((val - 1) * 100 * 10) / 10;

  return percent % 1 === 0 ? String(percent) : percent.toFixed(1);
};

const EditablePercentCell = ({
  value,
  onChange,
  isDirty,
}: EditablePercentCellProps) => {
  const displayValue = formatPercent(value);
  const isZero = displayValue === '0';
  const [localValue, setLocalValue] = React.useState(
    isZero ? '' : displayValue
  );
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    if (!isFocused) {
      const computed = formatPercent(value);

      setLocalValue(computed === '0' ? '' : computed);
    }
  }, [value, isFocused]);

  const handleCommit = () => {
    setIsFocused(false);
    const raw = localValue.trim() === '' ? '0' : localValue;
    const parsed = Number.parseFloat(raw);

    if (Number.isNaN(parsed)) {
      const computed = formatPercent(value);

      setLocalValue(computed === '0' ? '' : computed);

      return;
    }

    const multiplier = 1 + parsed / 100;

    if (multiplier !== value) {
      onChange(multiplier);
    }
  };

  return (
    <Input
      value={localValue}
      placeholder="0"
      startAdornment={
        isDirty ? <span className="size-1.5 rounded-full bg-navy-500" /> : null
      }
      endAdornment={<span className="text-xs text-gray-500">%</span>}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      inputCustomClass="py-1 text-sm text-right"
      inputWrapperCustomClass="w-[80px]"
      aria-label="Percentage value"
      data-testid="editable-percent-input"
    />
  );
};

export default EditablePercentCell;
